import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { googleYouTubeApi } from 'api/connect/google'
import { useTranslation } from 'react-i18next'
import { isErrorCode } from 'utils/helpers'
import ErrorMessage from 'components/Shared/ErrorMessage'

const YOUTUBE_WATCH_BASE = 'https://www.youtube.com/watch?v='

function codifyYouTubeError(error) {
  const errorCode = error?.error_code
  return isErrorCode(errorCode) ? `common.serverErrors.${errorCode}` : null
}

export default function YouTubeUrlInput({ onVideo, onClear, video }) {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { t } = useTranslation()
  
  const handleValidate = useCallback(async () => {
    const trimmed = url.trim()
    if (!trimmed) return

    setLoading(true)
    setError(null)

    const { data, error: apiError } = await googleYouTubeApi.validateUrl(trimmed)

    setLoading(false)

    if (apiError) {
      const translationKey = codifyYouTubeError(apiError)
      setError(
        translationKey
          ? t(translationKey)
          : t('common.errors.inServerResponse'),
      )
      return
    }

    onVideo({
      source: 'youtube',
      youtube_video_id: data.video_id,
      url: `${YOUTUBE_WATCH_BASE}${data.video_id}`,
      type: 'video/youtube',
      title: data.title,
      duration_seconds: data.duration_seconds,
      thumbnail_url: data.thumbnail_url,
      channel_title: data.channel_title,
      warnings: data.warnings || [],
    })
  }, [url, onVideo])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleValidate()
      }
    },
    [handleValidate],
  )

  const handleClear = useCallback(() => {
    setUrl('')
    setError(null)
    onClear()
  }, [onClear])

  // If a video has already been validated, show the preview card
  if (video) {
    return (
      <div className="youtube-video-preview">
        <div className="youtube-video-preview__card">
          {video.thumbnail_url && (
            <img
              className="youtube-video-preview__thumbnail"
              src={video.thumbnail_url}
              alt={video.title}
            />
          )}
          <div className="youtube-video-preview__info">
            <div className="youtube-video-preview__title">{video.title}</div>
            {video.channel_title && (
              <div className="youtube-video-preview__channel">{video.channel_title}</div>
            )}
            {video.duration_seconds != null && (
              <div className="youtube-video-preview__duration">
                {formatDuration(video.duration_seconds)}
              </div>
            )}
          </div>
          <button
            type="button"
            className="youtube-video-preview__remove"
            onClick={handleClear}
            aria-label="Remove video"
          >
            &times;
          </button>
        </div>
        {video.warnings && video.warnings.length > 0 && (
          <div className="youtube-video-preview__warnings">
            {video.warnings.map((w, i) => (
              <div key={i} className="youtube-video-preview__warning">
                ⚠ {w}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="youtube-url-input">
      <div className="youtube-url-input__row">
        <input
          className="youtube-url-input__field"
          type="url"
          placeholder="https://www.youtube.com/watch?v=..."
          value={url}
          onChange={(e) => {
            setUrl(e.target.value)
            setError(null)
          }}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <button
          type="button"
          className="youtube-url-input__btn"
          onClick={handleValidate}
          disabled={loading || !url.trim()}
        >
          {loading ? t('common.loading') : t('common.save')}
        </button>
      </div>
      {error && <ErrorMessage variant="light" classNames="mt-1">{error}</ErrorMessage>}
    </div>
  )
}

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

YouTubeUrlInput.propTypes = {
  onVideo: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  video: PropTypes.shape({
    youtube_video_id: PropTypes.string,
    title: PropTypes.string,
    channel_title: PropTypes.string,
    duration_seconds: PropTypes.number,
    thumbnail_url: PropTypes.string,
    warnings: PropTypes.arrayOf(PropTypes.string),
  }),
}

YouTubeUrlInput.defaultProps = {
  video: null,
}
